
import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichTextEditor } from "@/components/ui/rich-text-editor";

interface ServiceDetailsFormProps {
  formData: {
    name: string;
    description: string;
    price: string;
    additional_person?: string | number | null;
    location: string;
    email: string;
    phone: string;
    unit?: string;
  };
  setFormData: React.Dispatch<React.SetStateAction<any>>;
  editing: boolean;
  service: any;
  currency?: string;
}

export const ServiceDetailsForm = ({ 
  formData, 
  setFormData, 
  editing, 
  service,
  currency = "Ksh"
}: ServiceDetailsFormProps) => {
  const [showCustomUnit, setShowCustomUnit] = React.useState<boolean>(
    formData.unit && !["Hour", "Session", "Other"].includes(formData.unit)
  );
  
  const [customUnit, setCustomUnit] = React.useState<string>(
    (formData.unit && !["Hour", "Session", "Other"].includes(formData.unit)) 
      ? formData.unit 
      : ""
  );
  
  const handleUnitChange = (value: string) => {
    if (value === "Other") {
      setShowCustomUnit(true);
      setFormData(prev => ({ ...prev, unit: customUnit || "" }));
    } else {
      setShowCustomUnit(false);
      setFormData(prev => ({ ...prev, unit: value }));
    }
  };

  const handleCustomUnitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setCustomUnit(value);
    setFormData(prev => ({ ...prev, unit: value }));
  };
  
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="name">Service Name</Label>
        {editing ? (
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
            className="mt-1"
          />
        ) : (
          <p className="mt-1 text-sm">{service.name}</p>
        )}
      </div>
      
      <div>
        <Label htmlFor="description">Description</Label>
        {editing ? (
          <RichTextEditor
            value={formData.description}
            onChange={(value) => setFormData(prev => ({ ...prev, description: value }))}
            height={250}
            className="mt-1"
          />
        ) : (
          <div 
            className="mt-1 text-sm prose prose-sm max-w-none"
            dangerouslySetInnerHTML={{ __html: service.description || "No description provided" }}
          />
        )}
      </div>
      
      <div>
        <Label htmlFor="price">Price</Label>
        {editing ? (
          <div className="grid grid-cols-2 gap-4 mt-1">
            <Input
              id="price"
              type="number"
              value={formData.price}
              onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value }))}
              className="[&::-webkit-inner-spin-button]:appearance-none"
            />
            <div>
              <Select
                value={
                  formData.unit && ["Hour", "Session", "Other"].includes(formData.unit) 
                    ? formData.unit 
                    : formData.unit 
                      ? "Other" 
                      : "Hour"
                }
                onValueChange={handleUnitChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Unit" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Hour">Hour</SelectItem>
                  <SelectItem value="Session">Session</SelectItem>
                  <SelectItem value="Other">Other</SelectItem>
                </SelectContent>
              </Select>
              {showCustomUnit && (
                <Input
                  className="mt-2"
                  placeholder="Enter custom unit"
                  value={customUnit}
                  onChange={handleCustomUnitChange}
                />
              )}
            </div>
          </div>
        ) : (
          <p className="mt-1 text-sm">
            {service.price ? `${currency}${service.price}${service.unit ? `/${service.unit}` : "/hr"}` : "N/A"}
          </p>
        )}
      </div>
      
      <div>
        <Label htmlFor="additional_person">Price for each additional person</Label>
        {editing ? (
          <Input
            id="additional_person"
            type="number"
            value={formData.additional_person || ""}
            onChange={(e) => setFormData(prev => ({ 
              ...prev, 
              additional_person: e.target.value ? e.target.value : null 
            }))}
            className="mt-1 [&::-webkit-inner-spin-button]:appearance-none"
            placeholder="Optional"
          />
        ) : (
          <p className="mt-1 text-sm">
            {service.additional_person ? `${currency}${service.additional_person} for each additional person` : "N/A"}
          </p>
        )}
      </div>
      
      <div>
        <Label htmlFor="location">Location</Label>
        {editing ? (
          <Input
            id="location"
            value={formData.location}
            onChange={(e) => setFormData(prev => ({ ...prev, location: e.target.value }))}
            className="mt-1"
          />
        ) : (
          <p className="mt-1 text-sm">{service.location || "No location provided"}</p>
        )}
      </div>
      
      <div>
        <Label htmlFor="email">Contact Email</Label>
        {editing ? (
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
            className="mt-1"
          />
        ) : (
          <p className="mt-1 text-sm">{service.email || "No email provided"}</p>
        )}
      </div>
      
      <div>
        <Label htmlFor="phone">Contact Phone</Label>
        {editing ? (
          <Input
            id="phone"
            value={formData.phone}
            onChange={(e) => setFormData(prev => ({ ...prev, phone: e.target.value }))}
            className="mt-1"
          />
        ) : (
          <p className="mt-1 text-sm">{service.phone || "No phone provided"}</p>
        )}
      </div>
    </div>
  );
};
